import { NarutoItem } from "@/interfaces/CardItem";
import { SrcType } from "@/models/alt-arts";
import { AaMap } from "@/models/alt-arts";
import cardData from "@/data/naruto/card_data.json";
import aaJson from "@/data/naruto/aa_data.json";
import { BaseCardDataManager } from "@/lib/games/base/getCardDataBase";
import { getCardImgproxyUrl } from "@/lib/imgproxy";
import { cardImageUrl } from "@/lib/cdn";

const aa = aaJson as AaMap;

class NarutoCardDataManager extends BaseCardDataManager<NarutoItem> {
  protected readonly cdnGame = "naruto" as const;
  protected readonly imageVersion = "1.0";
  protected readonly defaultRegion = "en";

  /** Naruto only has a separate Japanese printing; everything else is "en". */
  protected getCorrectedRegion(region: SrcType | ""): string {
    return region.toLowerCase() === "ja" ? "ja" : "en";
  }

  constructor() {
    super(aa);
    // Initialize CARD_DATA with cleaned data
    this.CARD_DATA = this.clean(cardData);
  }

  public getCardNumber(cardId: string): string {
    let cardNumber = cardId;

    const underscoreCount = (cardId.match(/_/g) || []).length;
    if (underscoreCount === 1) {
      // id is like NRT-001_p1 for promo alternate art
      const [baseCardId, _altArtId] = cardId.split("_");
      cardNumber = baseCardId;
    } else {
      // simple card version like NRT-001
      cardNumber = cardId;
    }
    return cardNumber;
  }

  public isCardNumber(cardId: string): boolean {
    return !cardId.includes("_");
  }

  public getCardId(cardId: string): string | undefined {
    if (this.isCardNumber(cardId)) {
      const cardNumber = cardId;
      if (cardNumber in this.aa && this.aa[cardNumber].length > 0) {
        // assume first card is en and the base rare card
        const firstCard = this.aa[cardNumber][0];
        return firstCard.id;
      }
    } else {
      return cardId;
    }
  }

  public getCardData(cardId: string): NarutoItem | undefined {
    return this.CARD_DATA[cardId];
  }

  public cleanCardData(): Record<string, NarutoItem> {
    return this.CARD_DATA;
  }

  protected clean(
    data: Record<string, NarutoItem>,
  ): Record<string, NarutoItem> {
    let cleanedData: Record<string, NarutoItem> = {};
    for (const [k, v] of Object.entries(data)) {
      // Create a deep copy of the current item
      let item: NarutoItem = structuredClone(v);

      // Convert entrance_cost to a number if needed
      if (item.entrance_cost && typeof item.entrance_cost === "string") {
        item.entrance_cost = this.getIntFromStr(item.entrance_cost);
      }

      // Convert turn_cost to a number if needed
      if (item.turn_cost && typeof item.turn_cost === "string") {
        item.turn_cost = this.getIntFromStr(item.turn_cost);
      }

      // Convert hand_cost to a number if needed
      if (item.hand_cost && typeof item.hand_cost === "string") {
        item.hand_cost = this.getIntFromStr(item.hand_cost);
      }

      // Convert combat/support to numbers if needed
      if (typeof item.combat === "string") {
        item.combat = this.getIntFromStr(item.combat);
      }
      if (typeof item.support === "string") {
        item.support = this.getIntFromStr(item.support);
      }

      // Set cost field for cost divider
      if (item.entrance_cost !== undefined) {
        item.cost = item.entrance_cost;
      }

      cleanedData[k] = item;
    }
    return cleanedData;
  }
}

// Create singleton instance
const narutoManager = new NarutoCardDataManager();

// Export functions
export const getCardImage = (
  cardId: string,
  size: "original" | "316x440" | "158x220" | "90x125" = "original",
  src: SrcType | "" = "",
): string => narutoManager.getCardImage(cardId, size, src);

export const getCardData = (cardId: string): NarutoItem | undefined =>
  narutoManager.getCardData(cardId);

export const getAltArts = (cardId: string, showAAs: boolean): any[] =>
  narutoManager.getAltArts(cardId, showAAs);

export const getAACardId = (aaItem: any): string =>
  narutoManager.getAACardId(aaItem);

export const getAACardIdData = (aaCardId: string): NarutoItem | undefined =>
  narutoManager.getAACardIdData(aaCardId) as NarutoItem | undefined;

export const getAAFromCardId = (aaCardId: string): any =>
  narutoManager.getAAFromCardId(aaCardId);

export const cleanCardData = (): Record<string, NarutoItem> =>
  narutoManager.cleanCardData() as Record<string, NarutoItem>;
